import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { DriverLoadingPage, triggerToastDS } from "@qivia/ui";
import { ButtonDS } from "@qivia/ui/src/designSystem/components/Button";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectLastSession } from "../../qrCode/driverKnownSlice";
import {
  createInitialVehicleRestitutionAsync,
  selectCreateInitialVehicleRestitutionStatus,
  vehicleSessionsSlice,
} from "../vehicleRestitution/vehicleSessionsSlice";
import { Page } from "../../components/Page";
import { VehicleConditionForm } from "../shared/vehicleConditionForm";
import "../../../i18n/i18n";

interface CreateInitialRestitutionForLastSessionStepProps {
  mileage: number;
  setMileage: (value: number) => void;
  hasTelemetryMileage: boolean;
  isCabinCleaned: boolean;
  setIsCabinCleaned: Dispatch<SetStateAction<boolean>>;
  isTrunkCleaned: boolean;
  setIsTrunkCleaned: Dispatch<SetStateAction<boolean>>;
  observations: string;
  setObservations: (value: string) => void;
  photos: File[];
  setPhotos: (files: File[]) => void;
  onReturn: () => void;
}

export const CreateInitialRestitutionForLastSessionStep = (
  props: CreateInitialRestitutionForLastSessionStepProps,
) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const lastSession = useAppSelector(selectLastSession);
  const createInitialStatus = useAppSelector(
    selectCreateInitialVehicleRestitutionStatus,
  );

  const [displayError, setDisplayError] = useState<boolean>(false);

  useEffect(() => {
    if (createInitialStatus === "failed") {
      triggerToastDS(t("restitution.create.error.text") || "", "ERROR");
      dispatch(
        vehicleSessionsSlice.actions.resetCreateInitialVehicleRestitutionStatus(),
      );
    }
  }, [createInitialStatus, t, dispatch]);

  const onSubmit = useCallback(async () => {
    setDisplayError(props.mileage === 0);
    if (!lastSession || props.mileage === 0) return;
    const pictures = await Promise.all(
      props.photos.map(async (file) => {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const fileBuffer = btoa(
          Array.from(bytes, (b) => String.fromCharCode(b)).join(""),
        );
        return { originalFileName: file.name, fileBuffer };
      }),
    );
    void dispatch(
      createInitialVehicleRestitutionAsync({
        vehicleUuid: lastSession.vehicleUuid,
        mileage: props.mileage,
        isCabinCleaned: props.isCabinCleaned,
        isTrunkCleaned: props.isTrunkCleaned,
        observations: props.observations === "" ? null : props.observations,
        departureLocation: "",
        furthestDestination: null,
        withoutEndDate: true,
        pictures,
      }),
    );
  }, [
    dispatch,
    lastSession,
    props.mileage,
    props.isCabinCleaned,
    props.isTrunkCleaned,
    props.observations,
    props.photos,
  ]);

  if (createInitialStatus === "processing") return <DriverLoadingPage />;

  return (
    <Page
      title={t("stateBeforeDeparture.title")}
      returnButton
      returnButtonAction={props.onReturn}
      withoutHeaderSpacer
      helpButton
      bottomButtonBackgroundColor="white"
      withShadow
      bottomButton={
        <ButtonDS
          format="fill"
          buttonType="primary"
          text={t("restitution.button.validate")}
          disabled={props.mileage === 0}
          onClick={() => void onSubmit()}
        />
      }
    >
      <VehicleConditionForm
        mileage={props.mileage}
        setMileage={(value) => props.setMileage(value === "" ? 0 : value)}
        hasTelemetryMileage={props.hasTelemetryMileage}
        isCabinCleaned={props.isCabinCleaned}
        setIsCabinCleaned={props.setIsCabinCleaned}
        isTrunkCleaned={props.isTrunkCleaned}
        setIsTrunkCleaned={props.setIsTrunkCleaned}
        observations={props.observations}
        setObservations={props.setObservations}
        photos={props.photos}
        setPhotos={props.setPhotos}
        formError={displayError ? t("mileage.error.input") || "" : undefined}
      />
    </Page>
  );
};
